import { AxiosResponse } from 'axios';
import qs from 'qs';
import { axiosApiInstance } from 'Store';
import { convertPrice } from 'utils/convertPrice';
import {
    BailiffOfferDetailsResponse,
    BailiffOfferResponseContent,
    BailiffOffersResponse
} from 'utils/state-managment/bailiff/bailiffOffer';
import { BailiffFilterReturnData } from 'utils/types/Bailiff';
import { BailiffSavedFilter } from 'utils/types/Filter';
import { LocationItem } from 'utils/types/V2Locations';

import { activeNotification } from 'components/functions/activeNotification';

const getSavedFilters = async (): Promise< BailiffSavedFilter[]> => {
    return axiosApiInstance
        .get<BailiffSavedFilter[]>('/v2/bailiff-notice-offer/filter/list')
        .then((resp) => {
            if (resp.data) {
                return resp.data.map((el) => ({
                    ...el,
                    name: el.name
                }));
            }

            return [];
        })
        .catch((err) => {
            if (err?.response?.status === 401) {
                window.parent.location.reload();
            }

            return [];
        });
};

const getFilterDetailsBailiff = async (filterId: number): Promise<BailiffFilterReturnData> => {
    return axiosApiInstance
        .get(`/v2/bailiff-notice-offer/filter/details?id=${filterId}`)
        .then((resp) => {
            const filters = resp.data;
            // Backend zwraca oba pola: locations/officeLocations (stare ID, dla kompatybilności starej apki mobilnej)
            // i filterLocations/filterOfficeLocations (nowe OSM ID). Dla nowej apki czytamy filterLocations/filterOfficeLocations.
            const savedLocations = Array.isArray(filters.filterLocations)
                ? Object.fromEntries(
                    filters.filterLocations.map((location: LocationItem) => [
                        location.id,
                        // @ts-expect-error z Api dostajemy fullName
                        { ...location, label: location.fullName }
                    ])
                )
                : {};
            const savedOfficeLocations = Array.isArray(filters.filterOfficeLocations)
                ? Object.fromEntries(
                    filters.filterOfficeLocations.map((location: LocationItem) => [
                        location.id,
                        // @ts-expect-error z Api dostajemy fullName
                        { ...location, label: location.fullName }
                    ])
                )
                : {};

            return {

                ...filters,
                city: filters.locations ? filters.locations : [],
                officeCity: filters.officeLocations ? filters.officeLocations : [],
                wantedKeywords: filters.wantedKeywords ? filters.wantedKeywords : [],
                unwantedKeywords: filters.unwantedKeywords ? filters.unwantedKeywords : [],
                filterId: filterId,
                name: filters.name ? filters.name : '',
                pushAlert: filters.alertPush ? filters.alertPush : false,
                alertEmail: filters.alertEmail ? filters.alertEmail : false,
                alertSms: filters.alertSms ? filters.alertSms : false,
                module: filters.module ? filters.module : '',
                tossedUp: filters.tossedUp ? filters.tossedUp : false,
                notificationsDelay: filters.notificationsDelay !== null && filters.notificationsDelay !== undefined
                    ? filters.notificationsDelay.toString()
                    : '0',
                includeZeroArea: filters.includeZeroArea ? filters.includeZeroArea : false,
                offerAddedFrom: filters.offerAddedFrom ? filters.offerAddedFrom : '',
                offerAddedTo: filters.offerAddedTo ? filters.offerAddedTo : '',
                savedFilter: filters.savedFilter ? filters.savedFilter : false,
                locations: [],
                filterLocations: [],
                officeLocations: [],
                filterOfficeLocations: [],
                savedLocations: savedLocations,
                savedOfficeLocations: savedOfficeLocations,
                searchLocationResult: []
            };
        })
        .catch(() => {
            activeNotification('Problem z załadowaniem danych', 'Niestety nie udało się załadować danych. Spróbuj ponownie później', 'danger');
        });
};

const removeSavedFilter = (filterId: number) => {
    return axiosApiInstance
        .delete(`/v2/bailiff-notice-offer/filter/delete?id=${filterId}`)
        .then(() => {
            activeNotification('Udało się!', 'Filtr został usunięty', 'success');
        })
        .catch(() => {
            activeNotification('Problem z usunięciem', 'Niestety nie udało się usunąć danych. Spróbuj ponownie później', 'danger');
        });
};

const updateFilter = async (params: BailiffFilterReturnData) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { locations, filterLocations, officeLocations, filterOfficeLocations, ...paramsWithoutLocations } = params;

    const locationIds = Object.keys(params.savedLocations).map((id) => {
        return { id: Number(id) };
    });

    const officeLocationIds = Object.keys(params.savedOfficeLocations).map((id) => {
        return { id: Number(id) };
    });

    return axiosApiInstance({
        method: 'put',
        url: '/v2/bailiff-notice-offer/filter/edit',
        data: {
            ...paramsWithoutLocations,
            filterLocations: locationIds,
            filterOfficeLocations: officeLocationIds
        },
        paramsSerializer: function (params) {
            return qs.stringify(params, { arrayFormat: 'repeat' });
        }
    })
        .then((resp) => {
            activeNotification('Udało się!', 'Filtr został zaktualizowany', 'success');

            return resp;
        })
        .catch((err) => {
            activeNotification('Problem z zapisem', 'Niestety nie udało się zapisać danych. Spróbuj ponownie później', 'danger');

            return Promise.reject(err);
        });
};

const addFilter = (params: BailiffFilterReturnData): Promise<AxiosResponse<number>> => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { locations, filterLocations, officeLocations, filterOfficeLocations, ...paramsWithoutLocations } = params;

    const locationIds = Object.keys(params.savedLocations).map((id) => {
        return { id: Number(id) };
    });

    const officeLocationIds = Object.keys(params.savedOfficeLocations).map((id) => {
        return { id: Number(id) };
    });

    return axiosApiInstance({
        method: 'post',
        url: '/v2/bailiff-notice-offer/filter/create',
        data: {
            ...paramsWithoutLocations,
            filterLocations: locationIds,
            filterOfficeLocations: officeLocationIds
        },
        paramsSerializer: function (params) {
            return qs.stringify(params);
        }
    })
        .then((resp) => {
            activeNotification('Udało się!', 'Filtr został dodany', 'success');

            return resp;
        })
        .catch((err) => {
            if (err?.response?.status === 400) {
                activeNotification(
                    'Błąd',
                    err.response.data,
                    'danger'
                );

                return Promise.reject(err);
            } else {
                activeNotification(
                    'Coś poszło nie tak!',
                    'Spróbuj ponownie',
                    'danger'
                );

                return Promise.reject(err);
            }
        });
};

const getBailiffOffers = (params: BailiffFilterReturnData, page: number, size: number): Promise<BailiffOffersResponse> => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { locations, filterLocations, officeLocations, filterOfficeLocations, ...paramsWithoutLocations } = params;

    const locationIds = Object.keys(params.savedLocations).map((id) => {
        return { id: Number(id) };
    });

    const officeLocationIds = Object.keys(params.savedOfficeLocations).map((id) => {
        return { id: Number(id) };
    });

    return axiosApiInstance({
        method: 'post',
        url: '/v2/bailiff-notice-offer/advertisement',
        params: {
            page,
            size
        },
        data: {
            ...paramsWithoutLocations,
            filterLocations: locationIds,
            filterOfficeLocations: officeLocationIds
        }
    })
        .then((resp) => {
            if (resp.data) {
                return {
                    ...resp.data,
                    content: resp.data.content.map((el: BailiffOfferResponseContent) => ({
                        ...el,
                        city: el.location,
                        officeCity: el.officeLocation,
                        dateAdded: el.dateAdded,
                        estimateDate: el.estimateDate ? el.estimateDate : undefined,
                        reaction: {
                            likeStatus: el.reaction?.likeStatus,
                            unlikeStatus: el.reaction?.unlikeStatus,
                            viewedStatus: el.reaction?.viewedStatus,
                            outdatedStatus: el.reaction?.outdatedStatus,
                            arrangedStatus: el.reaction?.arrangedStatus,
                            phoneStatus: el.reaction?.phoneStatus,
                            hideStatus: el.reaction?.hideStatus,
                            openedStatus: el.reaction?.openedStatus
                        }
                    }))
                };
            }

            return { empty: true, content: [], afterSearch: true };
        })
        .catch(() => {
            activeNotification(
                'Nie znaleziono wyników',
                'Wyszukiwanie nie powiodło się, spróbuj jeszcze raz.',
                'warning'
            );

            return { empty: true, content: [], afterSearch: true };
        });
};

const getBailiffOfferDetails = async (id: number): Promise<BailiffOfferDetailsResponse> => {
    return axiosApiInstance
        .get(`/v2/bailiff-notice-offer/advertisement/details?id=${id}`)
        .then((resp) => {
            if (resp.data) {
                return {
                    ...resp.data
                };
            }
        })
        .catch(() => {
            activeNotification(
                'Nie udało się pobrać ogłoszenia',
                'Nie udało się pobrać danych szczegółowych ogłoszenia',
                'warning'
            );

            return [{ empty: true }];
        });
};

const getBailiffOfferByHashId = async (hash: string): Promise<BailiffOfferDetailsResponse> => {
    return axiosApiInstance
        .get(`/v2/bailiff-notice-offer/single-advertisement/detail?hash=${hash}`)
        .then((resp) => {
            if (resp.data) {
                return {
                    ...resp.data,
                    price: resp.data.price ? convertPrice(resp.data.price) : ''
                };
            }
        })
        .catch(() => {
            activeNotification(
                'Nie udało się pobrać ogłoszenia',
                'Nie udało się pobrać danych szczegółowych ogłoszenia',
                'warning'
            );

            return null;
        });
};

export const bailiffOffersApiRequests = {
    getSavedFilters,
    getFilterDetailsBailiff,
    removeSavedFilter,
    updateFilter,
    addFilter,
    getBailiffOffers,
    getBailiffOfferDetails,
    getBailiffOfferByHashId
};
